<script lang="ts" setup>
import { type IContentElement, logCompletion, logInteraction } from '@bosca/analytics-client-browser'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Bookmark from '~/assets/images/bookmark.svg?component'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Share from '~/assets/images/share.svg?component'

import { type ContentItemTypes } from '~/utils/graphql/graphql'

import { setBookmarkSaved } from '~/utils/mutations'
import { IsSavedDocument } from '~/utils/generated/graphql'
import { useGraphQuery } from '~/utils/graphql/client'
import { toLongDateString } from '~/utils/formatdate'

const props = defineProps<{
  item: ContentItemTypes
}>()

const user = useFirebaseUser()
const saved = ref(false)
const shareable = ref(true)
const contentItem: IContentElement = { id: props.item.id, type: props.item.__typename || '' }
const { data, refresh } = import.meta.client ? await useGraphQuery(IsSavedDocument, { id: props.item.id }, true) : { data: null, refresh: () => {} }

async function toggleBookmark() {
  if (!user.value || user.value.isAnonymous) {
    window.dispatchEvent(new Event('show-auth-prompt'))
    return
  }
  if (saved.value) {
    saved.value = false
    await setBookmarkSaved(props.item.id, false)
    await logInteraction({ id: 'bookmark-toggle', type: 'button', content: [contentItem], extras: { action: 'removed', slug: props.item.slug || '' } })
  }
  else {
    saved.value = true
    await setBookmarkSaved(props.item.id, true)
    await logInteraction({ id: 'bookmark-toggle', type: 'button', content: [contentItem], extras: { action: 'added', slug: props.item.slug || '' } })
  }
  window.dispatchEvent(new Event('refresh-header'))
}

function newShare(): ShareData {
  return {
    title: props.item.title,
    url: document.location.toString(),
  }
}

watch(user, () => {
  if (refresh) {
    refresh()
  }
})

async function onShare() {
  const share = newShare()
  if (navigator && navigator.canShare && navigator.canShare(share)) {
    navigator.share(share).then(() => {
      logCompletion({
        id: 'share',
        type: 'button',
        content: [{ id: props.item.id, type: props.item.__typename || '' }],
        extras: { slug: props.item.slug || '' },
      })
    }).catch((e) => {
      logCompletion({
        id: 'share',
        type: 'button',
        content: [{ id: props.item.id, type: props.item.__typename || '' }],
        extras: { error: 'true', message: e?.toString(), slug: props.item.slug || '' },
      })
    })
  }
  else {
    logCompletion({
      id: 'share',
      type: 'button',
      content: [{ id: props.item.id, type: props.item.__typename || '' }],
      extras: { error: 'true', message: 'unsupported', slug: props.item.slug || '' },
    })
  }
}

onMounted(() => {
  shareable.value = navigator && navigator.canShare && navigator.canShare(newShare())
  if (data) {
    watch(data, () => {
      saved.value = data.value?.profile.isSaved || false
    })
    saved.value = data.value?.profile.isSaved || false
  }
})
</script>

<template>
  <div :class="'flex items-center w-full ' + (item.__typename === 'Collection' ? '': 'mt-4') + ' m-auto'">
    <div
      v-if="item.__typename !== 'Collection'"
      class="uppercase text-sm"
    >
      {{ item.__typename === 'Article' && item.duration ? $t('nMins', { n: item.duration }) : typeof item.sessions !== 'undefined' ? $t('nDays', { n: item.sessions.length }) : toLongDateString(item.date) }}
    </div>
    <div
      v-if="item.__typename !== 'Collection'"
      class="grow"
    />
    <PButton
      v-if="shareable"
      id="share"
      :content="[{ id: item.id, type: item.__typename || '' }]"
      :extras="{ slug: item.slug || '' }"
      class="p-1 me-2 inline-block items-center justify-center justify-items-center stroke-black hover:stroke-blurple-700"
      :title="$t('share')"
      @click="onShare"
    >
      <span class="sr-only">{{ $t('share') }}</span>
      <Share :class="'w-6 h-6 stroke-black hover:stroke-blurple-700'" />
    </PButton>
    <PButton
      id="bookmark"
      :content="[{ id: item.id, type: item.__typename || '' }]"
      :extras="{ slug: item.slug || '' }"
      class="p-1 inline-block items-center justify-center justify-items-center focus:outline-none fill-black hover:fill-blurple-700 stroke-black hover:stroke-blurple-700"
      :title="$t('toggleBookmark')"
      @click="toggleBookmark"
    >
      <span class="sr-only">{{ $t('toggleBookmark') }}</span>
      <Bookmark :class="'w-6 h-6 stroke-black hover:stroke-blurple-700 ' + (saved ? 'fill-black hover:fill-blurple-700' : '')" />
    </PButton>
  </div>
</template>
